
import { Badge } from "@/components/ui/badge";
import { Award, BookOpen, Calendar, Database } from "lucide-react";

interface EvidenceQualityIndicatorProps {
  evidenceLevel: 'high' | 'medium' | 'low';
  publicationDate?: string;
  source: string;
  studyType?: string;
}

const EvidenceQualityIndicator = ({ 
  evidenceLevel, 
  publicationDate, 
  source,
  studyType 
}: EvidenceQualityIndicatorProps) => {
  const getQualityConfig = (level: string) => {
    switch (level) {
      case 'high':
        return {
          color: 'bg-green-100 text-green-800 border-green-200',
          icon: Award,
          label: 'High Quality Evidence',
          description: 'Systematic reviews, meta-analyses, or high-quality RCTs'
        };
      case 'medium':
        return {
          color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
          icon: BookOpen,
          label: 'Moderate Quality Evidence',
          description: 'Cohort studies, case-control studies, or lower-quality RCTs'
        };
      case 'low':
        return {
          color: 'bg-orange-100 text-orange-800 border-orange-200',
          icon: Database,
          label: 'Limited Quality Evidence',
          description: 'Case reports, expert opinion, or observational studies'
        };
      default:
        return {
          color: 'bg-gray-100 text-gray-800 border-gray-200',
          icon: Database,
          label: 'Evidence Level Unknown',
          description: 'Quality assessment pending'
        };
    }
  };

  const config = getQualityConfig(evidenceLevel);
  const IconComponent = config.icon;

  const formatDate = (date: string) => {
    if (!date) return null;
    return new Date(date).toLocaleDateString('en-GB', { 
      year: 'numeric', 
      month: 'short' 
    });
  };

  return (
    <div className="flex flex-col space-y-2">
      <div className="flex items-center space-x-2">
        <Badge className={`${config.color} border font-medium`}>
          <IconComponent className="w-3 h-3 mr-1" />
          {config.label}
        </Badge>
        <Badge variant="outline" className="text-xs">
          {source.toUpperCase()}
        </Badge>
        {publicationDate && (
          <Badge variant="outline" className="text-xs">
            <Calendar className="w-3 h-3 mr-1" />
            {formatDate(publicationDate)}
          </Badge>
        )}
      </div>
      
      <p className="text-xs text-gray-600">{config.description}</p>
      
      {studyType && (
        <div className="text-xs text-gray-500">
          Study Type: {studyType}
        </div>
      )}
    </div>
  );
};

export default EvidenceQualityIndicator;
